import React, { useState, useEffect } from 'react';
import { 
  getUsersFromOrganizationForAgenturen, 
  addUserToTeam, 
  removeUserFromTeam, 
  updateUserRole, 
  deleteUser, 
  updateUserData, 
  toggleUserStatus 
} from '../../api';
import AddMemberForm from '../forms/AddMemberForm';
import EditUserDialog from '../../components/common/EditUserDialog';
import CreateUser from '../CreateUser'; // NEU: Import hinzugefügt

function AgenturMembersManagement({ user, agenturId, onBack }) {
  const [members, setMembers] = useState([]);
  const [allMembers, setAllMembers] = useState([]); // Ungefilterte Mitglieder
  const [teams, setTeams] = useState([]); // Kann entfernt werden, aber für eventuelle spätere Nutzung belassen
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddMemberForm, setShowAddMemberForm] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);
  const [agentur, setAgentur] = useState(null);

  // Filter-States für Name, Team, Projekt
  const [filterName, setFilterName] = useState('');
  const [filterTeam, setFilterTeam] = useState('');
  const [filterProjekt, setFilterProjekt] = useState('');

  const [filters, setFilters] = useState({
    sortBy: 'full_name',
    sortOrder: 'asc',
    teamFilter: '',
  });

  // Hilfsfunktionen für Dropdown-Optionen
  const uniqueValues = (arr, key) => {
    return Array.from(new Set(arr.map(item => item[key]).filter(Boolean)));
  };

  // Filter-Handler
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      sortBy: 'full_name',
      sortOrder: 'asc',
      teamFilter: '',
    });
  };

  // Mitglieder laden (wie Organization, aber für Agenturen)
  const loadAllMembers = async () => {
    setLoading(true);
    setError('');
    try {
      const agenturDaten = await getUsersFromOrganizationForAgenturen(user.organization_id);
      if (agenturDaten.success && Array.isArray(agenturDaten.agenturen)) {
        const found = agenturDaten.agenturen.find(a => a.agentur.id.toString() === agenturId.toString());
        if (found) {
          setAgentur(found.agentur);
          setAllMembers(found.mitglieder || []);
          // Debug-Ausgabe
          console.log('Agentur Mitglieder:', found.mitglieder);
          // setTeams(found.teams || []); // Falls Teams unterstützt werden
        } else {
          setError('Agentur nicht gefunden.');
          setAgentur(null);
          setAllMembers([]);
        }
      } else {
        setError(agenturDaten.error || 'Fehler beim Laden der Agentur.');
        setAgentur(null);
        setAllMembers([]);
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
      setAgentur(null);
      setAllMembers([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter anwenden (wie Organization)
  const applyFilters = () => {
    let filteredMembers = [...allMembers];
    // Name/E-Mail Filter
    if (filterName) {
      filteredMembers = filteredMembers.filter(member =>
        (member.full_name && member.full_name.toLowerCase().includes(filterName.toLowerCase())) ||
        (member.email && member.email.toLowerCase().includes(filterName.toLowerCase()))
      );
    }
    // Team-Filter
    if (filterTeam) {
      filteredMembers = filteredMembers.filter(member => 
        (member.a_team_name || member.team_name) === filterTeam
      );
    }
    // Projekt-Filter
    if (filterProjekt) {
      filteredMembers = filteredMembers.filter(member => 
        member.projekt === filterProjekt
      );
    }
    // Sortierung wie gehabt
    filteredMembers.sort((a, b) => {
      let compareValue = 0;
      switch (filters.sortBy) {
        case 'full_name':
          compareValue = (a.full_name || '').localeCompare(b.full_name || '');
          break;
        case 'created_at':
          compareValue = new Date(a.created_at || 0) - new Date(b.created_at || 0);
          break;
        default:
          compareValue = (a.full_name || '').localeCompare(b.full_name || '');
      }
      return filters.sortOrder === 'desc' ? -compareValue : compareValue;
    });
    setMembers(filteredMembers);
  };

  useEffect(() => {
    if (agenturId) {
      loadAllMembers();
      // setTeams([]); // Falls Teams unterstützt werden
    }
  }, [agenturId]);

  useEffect(() => {
    applyFilters();
  }, [allMembers, filters, filterName, filterTeam, filterProjekt]);

  // Rollenname
  const getRoleName = (roleType) => {
    switch (parseInt(roleType, 10)) {
      case 1: return 'Administrator';
      case 2: return 'Teamleiter';
      case 3: return 'Organisationsleiter';
      case 4: return 'Callcenter Leader';
      case 5: return 'Callcenter Mitarbeiter';
      case 6: return 'Fachberater';
      case 7: return 'Agentur Leiter';
      default: return 'Benutzer';
    }
  };

  // Benutzer bearbeiten
  const handleEditUser = (member) => {
    console.log("Member To edit : ", member)
    setSelectedUserForEdit(member);
    setShowEditUserDialog(true);
  };

  const handleEditUserSuccess = () => {
    setShowEditUserDialog(false);
    setSelectedUserForEdit(null);
    loadAllMembers();
  };

  const handleEditUserCancel = () => {
    setShowEditUserDialog(false);
    setSelectedUserForEdit(null);
  };

  // Benutzerstatus umschalten
  const handleToggleUserStatus = async (member) => {
    if (!window.confirm(`Möchten Sie ${member.full_name} wirklich ${member.active === 1 ? 'deaktivieren' : 'aktivieren'}?`)) {
      return;
    }
    setLoading(true);
    try {
      const newActiveStatus = member.active === 1 ? 0 : 1;
      const response = await toggleUserStatus(user.id, member.id, newActiveStatus);
      if (response.success) {
        loadAllMembers();
      } else {
        setError(response.error || 'Fehler beim Umschalten des Benutzerstatus');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Neuen Benutzer speichern
  const handleSaveMember = (response) => {
    if (response.success) {
      setShowAddMemberForm(false);
      loadAllMembers();
    }
  };

  if (!agenturId) {
    return (
      <div className="organization-members-management">
        <div className="page-header">
          <button className="back-button" onClick={onBack}>
            ← Zurück
          </button>
          <h2>Mitglieder verwalten</h2>
        </div>
        <div className="error-message">
          Keine Agentur-ID angegeben.
        </div>
      </div>
    );
  }

  return (
    <div className="organization-members-management">
      <div className="page-header">
        <button className="back-button" onClick={onBack}>
          ← Zurück
        </button>
        <h2>Mitglieder verwalten {agentur ? `- ${agentur.name}` : ''}</h2>
      </div>
      {error && <div className="error-message">{error}</div>}
      {loading && allMembers.length === 0 ? (
        <div className="loading">Mitglieder werden geladen...</div>
      ) : (
        <>
          {/* NEU: Members Actions Button hinzugefügt */}
          <div className="members-actions">
            <button
              className="add-member-button"
              onClick={() => setShowAddMemberForm(true)}
            >
              + Neues Mitglied
            </button>
          </div>

          {/* Filterleiste wie bei Leads */}
          <div className="leads-filters">
            <div className="filter-row">
              <input
                type="text"
                placeholder="Name oder E-Mail suchen..."
                value={filterName}
                onChange={e => setFilterName(e.target.value)}
                style={{ marginRight: '1rem', padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e0' }}
              />
              {/* Sortierung und Reihenfolge wie gehabt */}
              <div className="filter-group">
                <label>Projekte:</label>
                <select
                  value={filterProjekt}
                  onChange={e => setFilterProjekt(e.target.value)}
                  style={{ marginRight: '1rem', padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e0' }}
                >
                  <option value="">Alle Projekte</option>
                  {uniqueValues(allMembers, 'projekt').map(projekt => (
                    <option key={projekt} value={projekt}>{projekt}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Teams:</label>
                <select
                  value={filterTeam}
                  onChange={e => setFilterTeam(e.target.value)}
                  style={{ marginRight: '1rem', padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e0' }}
                >
                  <option value="">Alle Teams</option>
                  {uniqueValues(allMembers, 'a_team_name').map(team => (
                    <option key={team} value={team}>{team}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Sortieren nach:</label>
                <select 
                  value={filters.sortBy} 
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                >
                  <option value="full_name">Name</option>
                  <option value="created_at">Erstellungsdatum</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Reihenfolge:</label>
                <select 
                  value={filters.sortOrder} 
                  onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                >
                  <option value="asc">Aufsteigend</option>
                  <option value="desc">Absteigend</option>
                </select>
              </div>
              <div className="filter-group">
                <button className="reset-filters-button" onClick={() => { resetFilters(); setFilterName(''); setFilterTeam(''); setFilterProjekt(''); }}>
                  Filter zurücksetzen
                </button>
              </div>
            </div>
          </div>
          <div className="members-table-container">
            <table className="members-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>E-Mail</th>
                  <th>Rolle</th>
                  <th>Projekt</th>
                  <th>Team</th>
                  <th>Status</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {members.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="no-data">
                      {filters.teamFilter || filterTeam || filterName ? 
                        'Keine Mitglieder mit den aktuellen Filtern gefunden' : 
                        'Keine Mitglieder gefunden'
                      }
                    </td>
                  </tr>
                ) : (
                  members.map(member => (
                    <tr key={member.id}>
                      <td>{member.full_name || 'Kein Name'}</td>
                      <td>{member.email}</td>
                      <td>
                        {getRoleName(member.role_type)}
                      </td>
                      <td>
                        {member.projekt}
                      </td>
                      <td>
                        {member.a_team_name || 'Kein Team'}
                        {console.log("member : ", member)}
                      </td>
                      <td>
                        <span className={`status-badge ${member.active === 1 ? 'active' : 'inactive'}`}>
                          {member.active === 1 ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </td>
                      <td>
                        <div className="member-actions">
                          <button
                            className="edit-user-button"
                            onClick={() => handleEditUser(member)}
                            title="Benutzer bearbeiten"
                          >
                            Bearbeiten
                          </button>
                          <button
                            className={`toggle-status-button ${member.active === 1 ? 'deactivate' : 'activate'}`}
                            onClick={() => handleToggleUserStatus(member)}
                            title={member.active === 1 ? 'Benutzer deaktivieren' : 'Benutzer aktivieren'}
                          >
                            {member.active === 1 ? 'Deaktivieren' : 'Aktivieren'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      {showAddMemberForm && (
        <div className="modal-overlay">
          <div className="modal-dialog">
            <h3>Neues Mitglied hinzufügen</h3>
            {/* NEU: CreateUser Komponente statt AddMemberForm verwenden */}
            <CreateUser
              user={user}
              onClose={() => {
                setShowAddMemberForm(false);
                loadAllMembers();
              }}
              preselectedAgenturId={agenturId}
              hideOrganizationFields={true} // NEU: Organisationsfelder ausblenden
            />
          </div>
        </div>
      )}
      {showEditUserDialog && selectedUserForEdit && (
        <EditUserDialog
          user={user}
          targetUser={selectedUserForEdit}
          onSave={handleEditUserSuccess}
          onCancel={handleEditUserCancel}
          isMobile={false}
          isTablet={false}
        />
      )}
    </div>
  );
}

export default AgenturMembersManagement;